/**
 * An icon from the NexERP set (icons.js).
 *
 * Line icons on a 24px grid, drawn in `currentColor`, so an icon takes the
 * colour of the text around it and follows both themes with no per-theme
 * artwork. Decorative by default (hidden from assistive technology): the text
 * beside an icon is what gets read. Pass `label` for the rare icon that stands
 * alone and carries meaning — it then becomes an image with that name.
 *
 * An unknown name renders nothing rather than throwing: a missing glyph must
 * never take a screen down with it.
 */
import { createElement } from 'react';
import { ICONS } from './icons.js';

/**
 * Icons that point along the reading direction. They are mirrored under
 * <html dir="rtl"> (styles/base.css `.icon-dir`), so "next" still points the
 * way the text flows in Arabic and the sidebar glyph sits on the sidebar's
 * side. Up/down arrows and everything else are direction-neutral.
 */
const DIRECTIONAL = new Set([
  'chevron-right',
  'chevron-left',
  'arrow-right',
  'arrow-left',
  'arrow-up-right',
  'external-link',
  'log-out',
  'panel-left',
  'panel-left-close',
  'panel-left-open',
]);

export function Icon({ name, size = 16, strokeWidth = 1.75, label, className = '', style }) {
  const nodes = ICONS[name];
  if (!nodes) return null;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`icon ${DIRECTIONAL.has(name) ? 'icon-dir' : ''} ${className}`}
      style={style}
      focusable="false"
      aria-hidden={label ? undefined : 'true'}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      {nodes.map(([tag, attrs], index) => createElement(tag, { key: index, ...attrs }))}
    </svg>
  );
}

/** True when the set has an icon by this name — for data-driven icon choices. */
export const hasIcon = (name) => Boolean(ICONS[name]);
